import React, { forwardRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { TextField } from '@mui/material';
import { getNestedError } from '../../../utils';

/**
 * Applies a mask pattern to a raw value.
 * '9' in the mask means any digit. Other characters are literals.
 */
const applyMask = (rawValue, mask) => {
    const digits = rawValue.replace(/[^0-9]/g, '');
    let result = '';
    let digitIndex = 0;

    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
        if (mask[i] === '9') {
            result += digits[digitIndex];
            digitIndex++;
        } else {
            result += mask[i];
        }
    }

    return result;
};

/**
 * Generate a placeholder hint from the mask, e.g. '(999) 999-9999' -> '(___) ___-____'
 */
const getPlaceholderFromMask = (mask) => {
    return mask.replace(/9/g, '_');
};

export const MaskInputCustom = forwardRef(
    (
        {
            className,
            name,
            label,
            value,
            error,
            mask,
            styles,
            control,
            rules,
            originalValue,
            noErrorMessage,
            helperText,
            disabled,
            readOnly,
        },
        ref
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const currentMask = mask || '(999) 999-9999';
        const placeholder = getPlaceholderFromMask(currentMask);

        return (
            <div className={`main-input-wrapper  ${className ? className : ''} ${readOnly ? 'read-only' : ''}`}>
                <Controller
                    control={control}
                    name={name}
                    rules={rules}
                    render={({ field, formState: { errors } }) => {
                        const error = getNestedError(errors, name);

                        const displayValue = applyMask(field?.value || '', currentMask);

                        const maxDigits = (currentMask.match(/9/g) || []).length;

                        const handleChange = (e) => {
                            const inputVal = e.target.value;
                            if (originalValue) {
                                field.onChange(inputVal);
                            } else {
                                field.onChange(inputVal.replace(/[^0-9]/g, '').slice(0, maxDigits));
                            }
                        };

                        return (
                            <>
                                <TextField
                                    ref={ref}
                                    value={displayValue}
                                    onChange={handleChange}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                    focused={isFocused}
                                    variant={'outlined'}
                                    label={label}
                                    placeholder={isFocused && !field?.value ? placeholder : undefined}
                                    error={!!error?.message}
                                    helperText={helperText}
                                    disabled={disabled || readOnly}
                                    InputLabelProps={{
                                        shrink: isFocused || !!field?.value,
                                    }}
                                />
                                {!noErrorMessage && <p className="error-message">{error?.message}</p>}
                            </>
                        );
                    }}
                />
            </div>
        );
    }
);

MaskInputCustom.displayName = 'MaskInputCustom';
